// Percorso file: src/components/UniformReport.jsx
//
// IL REPORT DELLE DIVISE — la finestra che il pulsante «Stats» della pagina
// Uniform Distribution apre al SOLO SUPERADMIN (UNIFORMS-UI / Fetta STATS,
// 19 settembre 2026; decisioni del committente in UNIF3-12 §1).
//
// ⚠️ QUESTO COMPONENTE NON CALCOLA E NON LEGGE NULLA. Riceve dalla pagina i
// dati che la pagina ha GIÀ — l'elenco di `GET /users`, il catalogo dell'anno
// guardato, la lista delle taglie, l'anno — e li passa alla funzione pura
// `reportDivise` (utils/statistiche-divise.js): nessuna `api.get`, nessun
// PUT; le consegne dell'anno le legge `consegneDellAnnata` (utils/uniforms.js).
// Il grosso è provato con node sul modulo puro; qui si prova che si disegni.
//
// ⚠️ SOLO A SCHERMO: niente Excel, niente file, niente download.
//
// LA FORMA (calco di UniformConfig, la modale gemella): guscio `qpsc-modal`
// del kit con la deroga di larghezza `.qpsc-modal.ur-modal` a 960px sopra i
// 768px (forma Payments, doc 09 §4), titolo «Uniform report — {anno}»,
// corpo che scorre, un solo comando «Close» (fantasma). Le tabelle larghe
// stanno in un contenitore a scorrimento orizzontale con la PRIMA COLONNA
// FISSA (forma Coach Attendance, come le tabelle di spunte della modale di
// configurazione).
//
// NUMERI VERI E STIME, DISTINTI (§1-bis dell'incarico): i conteggi «to
// deliver / delivered / not delivered» e le «delivered by size» sono reali;
// SOLO le taglie del non consegnato e il «to order» sono stime — e si
// mostrano con la classe `ur-est` (testo secondario, corsivo) e la parola
// «est.» accanto, mai come un numero pieno. Dove la stima non c'è si legge
// «n/a» e una nota lo dice.
import React, { useMemo, useState } from "react";
import "./AdminTools.css";
import "./UniformReport.css";
import {
  reportDivise,
  SENZA_TAGLIA,
  ALTRA_TAGLIA
} from "../utils/statistiche-divise";

// TESTI A SCHERMO (inglese, CLAUDE.md §3.7).
const ETICHETTA_SENZA_TAGLIA = "(no size)";
const ETICHETTA_ALTRA_TAGLIA = "Other";
const ETICHETTA_STIMA = "est.";
const ETICHETTA_NON_DISPONIBILE = "n/a";
const MSG_PERIMETRO = (anno) =>
  `Players only — active player cards of ${anno}. Coaches are not included.`;
const MSG_STIMA =
  "Figures marked “est.” are estimates, not real data: the sizes of undelivered items are split in proportion to the sizes already delivered to the same team (largest-remainder rounding). Where deliveries are too few to estimate, sizes are left blank (“n/a”).";
const MSG_NESSUN_CAPO = "No kit items in this season's catalogue.";
const MSG_NESSUNA_SQUADRA = "No active players with a team this season.";

// Un numero a schermo: lo zero si mostra come trattino, così la tabella non
// è un muro di zeri (calco «-» delle celle vuote della pagina).
const numero = (n) => (n > 0 ? n : "–");

const etichettaTaglia = (taglia) => {
  if (taglia === SENZA_TAGLIA) return ETICHETTA_SENZA_TAGLIA;
  if (taglia === ALTRA_TAGLIA) return ETICHETTA_ALTRA_TAGLIA;
  return taglia;
};

/*
 * LE COLONNE TAGLIA da mostrare: le taglie note, «(no size)», e «Other» SOLO
 * se almeno una voce ha una consegna con taglia fuori lista — altrimenti
 * una colonna vuota direbbe che esiste un caso che non esiste.
 */
const colonneDaMostrare = (report) => {
  const voci = [
    ...Object.values(report.perCapo),
    ...report.perSquadra.flatMap((b) => Object.values(b.perCapo))
  ];
  const altre = voci.some((v) => v.consegnatePerTaglia[ALTRA_TAGLIA] > 0);
  return report.colonneTaglia.filter((t) => t !== ALTRA_TAGLIA || altre);
};

/* Una cella STIMATA: il numero in forma «est.», o «n/a» se non c'è. */
const CellaStima = ({ valore, stimata, disponibile }) => {
  if (!disponibile) {
    return <td className="ur-td ur-est">{ETICHETTA_NON_DISPONIBILE}</td>;
  }
  if (!(valore > 0)) return <td className="ur-td">–</td>;
  if (!stimata) return <td className="ur-td">{valore}</td>;
  return (
    <td className="ur-td ur-est">
      {valore} <span className="ur-est__tag">{ETICHETTA_STIMA}</span>
    </td>
  );
};

/*
 * LA TABELLA DI UN INSIEME DI VOCI (il totale generale, o una squadra): una
 * riga per capo con i tre conteggi reali e le consegne per taglia; sotto,
 * la riga «to order» con le taglie stimate. Due righe per capo, perché
 * numeri veri e stime non stiano mai nella stessa cella.
 */
const TabellaVoci = ({ capi, perCapo, colonne, idTabella }) => (
  <div className="ur-table-wrap">
    <table className="ur-table ur-table--paired" id={idTabella}>
      <thead>
        <tr>
          <th className="ur-th ur-col-name" scope="col">Item</th>
          <th className="ur-th" scope="col">To deliver</th>
          <th className="ur-th" scope="col">Delivered</th>
          <th className="ur-th" scope="col">Not delivered</th>
          {colonne.map((taglia) => (
            <th className="ur-th" scope="col" key={taglia || "no-size"}>
              {etichettaTaglia(taglia)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {capi.map((capo) => {
          const voce = perCapo[capo.id];
          const stima = voce.stima;
          return (
            <React.Fragment key={capo.id}>
              <tr className="ur-row">
                <th className="ur-td ur-col-name" scope="row">{capo.label}</th>
                <td className="ur-td ur-td--strong">{numero(voce.daConsegnare)}</td>
                <td className="ur-td">{numero(voce.consegnate)}</td>
                <td className="ur-td">{numero(voce.nonConsegnate)}</td>
                {colonne.map((taglia) => (
                  <td className="ur-td" key={taglia || "no-size"}>
                    {numero(voce.consegnatePerTaglia[taglia] || 0)}
                  </td>
                ))}
              </tr>
              {/* La riga «to order»: reali + stimate per taglia, tutta in
                  forma di stima quando lo è; «n/a» dove la stima non c'è;
                  e il conto dei pezzi che nessuna squadra sa stimare. */}
              <tr className="ur-row ur-row--order">
                <th className="ur-td ur-col-name ur-order-label" scope="row">
                  <span className="ur-order-label__text">to order</span>
                  {stima.stimata && (
                    <span className="ur-est__tag"> {ETICHETTA_STIMA}</span>
                  )}
                  {!stima.disponibile ? (
                    <span className="ur-order-label__note">
                      {" "}
                      (no size estimate: no delivered sizes)
                    </span>
                  ) : stima.nonStimabili > 0 ? (
                    <span className="ur-order-label__note">
                      {" "}
                      (+{stima.nonStimabili} without size estimate)
                    </span>
                  ) : null}
                </th>
                <td className="ur-td" />
                <td className="ur-td" />
                <td className="ur-td" />
                {colonne.map((taglia) => (
                  <CellaStima
                    key={taglia || "no-size"}
                    valore={stima.daOrdinarePerTaglia?.[taglia] || 0}
                    stimata={stima.stimata}
                    disponibile={stima.disponibile}
                  />
                ))}
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
);

/* Una tabella a due colonne (capo, numero) per le sezioni 1 e 3. */
const TabellaConteggi = ({ capi, perCapo, campo, intestazione }) => (
  <div className="ur-table-wrap">
    <table className="ur-table ur-table--narrow">
      <thead>
        <tr>
          <th className="ur-th ur-col-name" scope="col">Item</th>
          <th className="ur-th" scope="col">{intestazione}</th>
        </tr>
      </thead>
      <tbody>
        {capi.map((capo) => (
          <tr className="ur-row" key={capo.id}>
            <th className="ur-td ur-col-name" scope="row">{capo.label}</th>
            <td className="ur-td ur-td--strong">{numero(perCapo[capo.id][campo])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* Le consegne per taglia (sezione 2): capi × taglie, più il totale. */
const TabellaConsegnatePerTaglia = ({ capi, perCapo, colonne }) => (
  <div className="ur-table-wrap">
    <table className="ur-table">
      <thead>
        <tr>
          <th className="ur-th ur-col-name" scope="col">Item</th>
          {colonne.map((taglia) => (
            <th className="ur-th" scope="col" key={taglia || "no-size"}>
              {etichettaTaglia(taglia)}
            </th>
          ))}
          <th className="ur-th" scope="col">Total delivered</th>
        </tr>
      </thead>
      <tbody>
        {capi.map((capo) => {
          const voce = perCapo[capo.id];
          return (
            <tr className="ur-row" key={capo.id}>
              <th className="ur-td ur-col-name" scope="row">{capo.label}</th>
              {colonne.map((taglia) => (
                <td className="ur-td" key={taglia || "no-size"}>
                  {numero(voce.consegnatePerTaglia[taglia] || 0)}
                </td>
              ))}
              <td className="ur-td ur-td--strong">{numero(voce.consegnate)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

/* Il «da ordinare» (sezione 4): capi × taglie, tutto stima dove lo è. */
const TabellaDaOrdinare = ({ capi, perCapo, colonne }) => (
  <div className="ur-table-wrap">
    <table className="ur-table">
      <thead>
        <tr>
          <th className="ur-th ur-col-name" scope="col">Item</th>
          {colonne.map((taglia) => (
            <th className="ur-th" scope="col" key={taglia || "no-size"}>
              {etichettaTaglia(taglia)}
            </th>
          ))}
          <th className="ur-th" scope="col">Note</th>
        </tr>
      </thead>
      <tbody>
        {capi.map((capo) => {
          const stima = perCapo[capo.id].stima;
          return (
            <tr className="ur-row" key={capo.id}>
              <th className="ur-td ur-col-name" scope="row">
                {capo.label}
                {stima.stimata && (
                  <span className="ur-est__tag"> {ETICHETTA_STIMA}</span>
                )}
              </th>
              {colonne.map((taglia) => (
                <CellaStima
                  key={taglia || "no-size"}
                  valore={stima.daOrdinarePerTaglia?.[taglia] || 0}
                  stimata={stima.stimata}
                  disponibile={stima.disponibile}
                />
              ))}
              <td className="ur-td ur-td--note">
                {!stima.disponibile
                  ? "estimate not available (no delivered sizes)"
                  : stima.nonStimabili > 0
                    ? `+${stima.nonStimabili} without size estimate`
                    : stima.motivo === "capo-senza-taglia"
                      ? "no size for this item"
                      : ""}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

/*
 * LA SEZIONE «PER SQUADRA» — un ACCORDION: una testata premibile per squadra
 * (nome e numero di giocatori), chiusa all'apertura; aperta, mostra la
 * tabella della squadra (`TabellaVoci`). Scelta dichiarata (UNIF3-12 §2.2):
 * con decine di squadre, tante tabelle aperte insieme sarebbero un muro; una
 * grande tabella squadra × capo × taglia non entrerebbe in nessuna
 * finestra. La testata è un `<button>` con `aria-expanded` (forma delle card
 * di Attendance Summary, `.qpsc-atts-card__head`), non uno `<span>` con
 * onClick. Più squadre possono stare aperte insieme.
 */
const SezioneSquadre = ({ report, colonne }) => {
  const [aperte, setAperte] = useState(() => new Set());

  const alterna = (squadra) =>
    setAperte((prima) => {
      const dopo = new Set(prima);
      if (dopo.has(squadra)) dopo.delete(squadra);
      else dopo.add(squadra);
      return dopo;
    });

  if (report.perSquadra.length === 0) {
    return <p className="qpsc-modal__note ur-muted">{MSG_NESSUNA_SQUADRA}</p>;
  }

  return (
    <div className="ur-teams">
      {report.perSquadra.map((blocco, indice) => {
        const aperta = aperte.has(blocco.squadra);
        const idPannello = `ur-team-${indice}`;
        return (
          <article className="ur-team" key={blocco.squadra}>
            <button
              type="button"
              className="ur-team__head"
              aria-expanded={aperta}
              aria-controls={idPannello}
              onClick={() => alterna(blocco.squadra)}
            >
              <span className="ur-team__name">{blocco.squadra}</span>
              <span className="ur-team__meta">
                {blocco.giocatori} {blocco.giocatori === 1 ? "player" : "players"}
              </span>
              <span className="ur-team__chevron" aria-hidden="true">
                {aperta ? "▲" : "▼"}
              </span>
            </button>
            {aperta && (
              <div id={idPannello} className="ur-team__body">
                <TabellaVoci
                  capi={report.capi}
                  perCapo={blocco.perCapo}
                  colonne={colonne}
                />
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
};

const UniformReport = ({ anno, utenti, config, sizes, onClose }) => {
  // Il calcolo vive nel modulo puro; qui si rifà solo se cambiano i dati.
  const report = useMemo(
    () => reportDivise({ utenti, config, anno, sizes }),
    [utenti, config, anno, sizes]
  );
  const colonne = useMemo(() => colonneDaMostrare(report), [report]);

  const titolo = Number.isInteger(anno)
    ? `Uniform report — ${anno}`
    : "Uniform report";

  return (
    <div className="qpsc-modal-overlay">
      {/* `ur-modal` è la classe MODIFICATORE di questa sola finestra: porta
          la deroga di larghezza sopra i 768px (UniformReport.css), come
          `uc-modal` per la configurazione e `qpsc-pay-modal` per Payments. */}
      <div className="qpsc-modal ur-modal" role="dialog" aria-labelledby="ur-title">
        <h2 id="ur-title" className="qpsc-modal__title">{titolo}</h2>
        <p className="qpsc-modal__note ur-centered">
          {MSG_PERIMETRO(anno)} {report.giocatori} {report.giocatori === 1 ? "player" : "players"} counted
          {report.giocatoriSenzaSquadra > 0
            ? ` (${report.giocatoriSenzaSquadra} without a team, not in any table).`
            : "."}
        </p>

        <div className="qpsc-modal__body">
          {report.capi.length === 0 ? (
            <p className="qpsc-modal__note ur-muted">{MSG_NESSUN_CAPO}</p>
          ) : (
            <>
              <section className="ur-section" aria-labelledby="ur-s1">
                <h3 id="ur-s1" className="ur-section__title">1. To deliver</h3>
                <TabellaConteggi
                  capi={report.capi}
                  perCapo={report.perCapo}
                  campo="daConsegnare"
                  intestazione="To deliver"
                />
              </section>

              <section className="ur-section" aria-labelledby="ur-s2">
                <h3 id="ur-s2" className="ur-section__title">2. Delivered by size</h3>
                <TabellaConsegnatePerTaglia
                  capi={report.capi}
                  perCapo={report.perCapo}
                  colonne={colonne}
                />
              </section>

              <section className="ur-section" aria-labelledby="ur-s3">
                <h3 id="ur-s3" className="ur-section__title">3. Not delivered</h3>
                <TabellaConteggi
                  capi={report.capi}
                  perCapo={report.perCapo}
                  campo="nonConsegnate"
                  intestazione="Not delivered"
                />
              </section>

              <section className="ur-section" aria-labelledby="ur-s4">
                <h3 id="ur-s4" className="ur-section__title">
                  4. To order <span className="ur-est__tag">({ETICHETTA_STIMA})</span>
                </h3>
                <p className="qpsc-modal__note ur-muted">{MSG_STIMA}</p>
                <TabellaDaOrdinare
                  capi={report.capi}
                  perCapo={report.perCapo}
                  colonne={colonne}
                />
              </section>

              <section className="ur-section" aria-labelledby="ur-s5">
                <h3 id="ur-s5" className="ur-section__title">5. By team</h3>
                <SezioneSquadre report={report} colonne={colonne} />
              </section>
            </>
          )}
        </div>

        <div className="qpsc-modal__actions">
          <button type="button" className="qpsc-ghost-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UniformReport;
